import { getBooleanField, getTextField } from '@lender/utils';
import UserFriendlyExpressionBuilder from 'app/services/formula/parser/userFriendlyExpressionBuilder';
import { camelCase, isNumber } from 'lodash';
import { array, object, string } from 'yup';

import {
  CompanyAuthorizedSignerMode,
  FormBuilderMethodType,
  FormBuilderOperationType,
  ReactRenderingContent,
  signingCapacityOptions,
  signingCompanyAuthorizedSignerTypeOption,
  signingLoanOfficerSignerTypeOption,
  signingSecurityGroupSignerTypeOption,
  SigningSignerType,
  signingSignerTypeOptions,
  signingTagsOptions,
  SigningTagType,
  signingTpoAuthorizedSignerTypeOption,
} from 'constants/formBuilder';
import { FormApplicationType, FormType } from 'constants/forms';
import { formatExpression } from 'utils/expressions';
import { getNewPdfElementPosition, isPdfFormType as checkIsPdfFormType } from 'utils/forms';
import { jsonStringify, parseJSONObject } from 'utils/json';

import { allowedAttributes, ATTRIBUTE_MAP, ATTRIBUTE_REGEX } from './Signature.constants';

const isRoleBasedSignerType = signerType => signerType?.value === SigningSignerType.ROLE_BASED;

export const isSecurityGroupSignerType = signerType =>
  signerType?.value === SigningSignerType.SECURITY_GROUP;

export const isCompanyAuthorizedSignerType = signerType =>
  signerType?.value === SigningSignerType.COMPANY_AUTHORIZED_SIGNER;

export const isSpecialSignerType = signerType =>
  isRoleBasedSignerType(signerType) ||
  isCompanyAuthorizedSignerType(signerType) ||
  isSecurityGroupSignerType(signerType);

export const schema = object().shape({
  hellosignTag: getTextField().when(['isMultipleOptions', 'signerType'], {
    is: (isMultipleOptions, signerType) => isMultipleOptions || isSpecialSignerType(signerType),
    then: schema => schema.notRequired(),
    otherwise: getTextField(),
  }),
  required: getBooleanField(),
  signerType: getTextField(),
  isMultipleOptions: getBooleanField({ isRequired: false }),
  selectionRequirement: getTextField().when('isMultipleOptions', {
    is: true,
    then: getTextField(),
    otherwise: schema => schema.notRequired(),
  }),
  parents: array()
    .of(
      object().shape({
        hellosignTag: getTextField(),
        children: array().of(
          object().shape({
            hellosignTag: getTextField(),
            required: getBooleanField({ isRequired: false }),
          }),
        ),
      }),
    )
    .when('isMultipleOptions', {
      is: true,
      then: schema => schema,
      otherwise: schema => schema.optional().nullable().strip(),
    }),
  signingRules: array()
    .of(
      object().shape({
        expression: getTextField(),
        capacity: getTextField(),
      }),
    )
    .when('signerType', {
      is: isRoleBasedSignerType,
      then: schema => schema.min(1, 'At least one signing rule is required'),
      otherwise: schema => schema.optional().nullable().strip(),
    }),
  securityGroup: object()
    .nullable()
    .when('signerType', {
      is: isSecurityGroupSignerType,
      then: schema => schema.required('Security group is required'),
      otherwise: schema => schema.notRequired().nullable(),
    }),
  authorizedSignerUserId: string()
    .nullable()
    .when(['signerType', 'authorizedSignerMode'], {
      is: (signerType, authorizedSignerMode) =>
        isCompanyAuthorizedSignerType(signerType) &&
        authorizedSignerMode?.value === CompanyAuthorizedSignerMode.SINGLE,
      then: schema => schema.required('Designated signer is required'),
      otherwise: schema => schema.notRequired(),
    }),
});

export const AUTHORIZED_SIGNER_MODE_OPTIONS = [
  { name: 'Any authorized signer', value: CompanyAuthorizedSignerMode.ANY },
  { name: 'Single designated signer', value: CompanyAuthorizedSignerMode.SINGLE },
];

export const getAuthorizedSignerModeOption = value =>
  AUTHORIZED_SIGNER_MODE_OPTIONS.find(opt => opt.value === value) ||
  AUTHORIZED_SIGNER_MODE_OPTIONS[0];

export const getSecurityGroupOption = (options, id) =>
  options.find(({ value }) => value === id) || null;

const normalizeAttributeName = attribute => {
  return camelCase(
    attribute
      .replace(ATTRIBUTE_REGEX, '')
      .replace(
        new RegExp(Object.keys(ATTRIBUTE_MAP).join('|'), 'g'),
        matched => ATTRIBUTE_MAP[matched],
      ),
  );
};

const normalizeAttributeValue = value =>
  ['true', 'false'].includes(value) ? value === 'true' : value;

export const extractDataAttributes = html => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const helloSignFields = doc.querySelectorAll(
    `[data-template-field-type="${ReactRenderingContent.SIGNATURE}"]`,
  );

  const extractedData = Array.from(helloSignFields).map(field => {
    const attributes = allowedAttributes.reduce((acc, attr) => {
      const fieldName = normalizeAttributeName(attr);
      const value = field.getAttribute(attr);
      acc[fieldName] = normalizeAttributeValue(value);

      return acc;
    }, {});

    return attributes;
  });

  const sortedData = extractedData.sort((a, b) => {
    return !!b.id - !!a.id;
  });

  return sortedData;
};

export const getDefaultHelloSignTree = () => {
  const newParentId = crypto.randomUUID();
  const newDependenceGroupId = crypto.randomUUID();

  return {
    parents: [
      {
        id: newParentId,
        children: [],
        dependenceGroupId: newDependenceGroupId,
        rootDependenceGroupId: newDependenceGroupId,
      },
    ],
  };
};

const getHtmlHelloSignTree = ({ html, id, parentId }) => {
  if (!id && !parentId) {
    return getDefaultHelloSignTree();
  }

  const extractedData = extractDataAttributes(html);
  const dependenceGroupId = extractedData.find(item =>
    [id, parentId].includes(item.id),
  )?.dependenceGroupId;

  const tree = extractedData.reduce(
    (acc, item) => {
      if (item.dependenceGroupId === dependenceGroupId) {
        acc.parents.push({
          ...item,
          dependenceGroupRequirement: item.dependenceGroupRequirement,
          hellosignTag: signingTagsOptions.find(({ value }) => value === item.tag),
          children: [],
        });

        acc.dependenceGroupRequirement = item.dependenceGroupRequirement;
      }

      if (item.dependenceGroupParentId) {
        acc.parents = acc.parents.map(parent => {
          if (parent.id === item.dependenceGroupParentId) {
            parent.children.push({
              ...item,
              hellosignTag: signingTagsOptions.find(({ value }) => value === item.tag),
            });
          }

          return parent;
        });
      }

      return acc;
    },
    {
      parents: [],
      dependenceGroupRequirement: null,
    },
  );

  return tree;
};

export const normalizeHelloSignTree = ({ isPdfFormType, pdfTree, html, id, parentId }) => {
  if (isPdfFormType) {
    return pdfTree;
  }

  const tree = getHtmlHelloSignTree({ html, id, parentId });
  const rootDependenceGroupId = tree.parents?.[0]?.dependenceGroupId;

  return {
    ...tree,
    parents: tree.parents?.map(parent => ({
      ...parent,
      rootDependenceGroupId: parent.rootDependenceGroupId || rootDependenceGroupId,
    })),
  };
};

export const getHelloSignerTypeOptionsByParams = ({
  formType,
  hellosignTag,
  isFFDisabled,
  applicationType,
}) => {
  const isSubcompanyScope = applicationType === FormApplicationType.BROKER_APPLICATION;
  const loanOfficerOptions =
    isFFDisabled || isSubcompanyScope ? [] : [signingLoanOfficerSignerTypeOption];
  const tpoAuthorizedSignerOptions = isSubcompanyScope
    ? [signingTpoAuthorizedSignerTypeOption]
    : [];
  // A subcompany-scoped form has no loan parties, so loan-party signers
  // (borrowers / non-borrower entities / loan officer) and the loan-party-based
  // Role-Based signer don't apply there — only company/security-group signers do.
  const scopedSignerTypeOptions = isSubcompanyScope ? [] : signingSignerTypeOptions;
  const baseOptions = scopedSignerTypeOptions
    .concat(loanOfficerOptions)
    .concat(signingSecurityGroupSignerTypeOption)
    .concat(isSubcompanyScope ? [signingCompanyAuthorizedSignerTypeOption] : []);

  const isNotPdfFormType = !checkIsPdfFormType(formType);

  const options = isNotPdfFormType
    ? baseOptions.map(({ value, ...rest }) => ({
        value: value
          .replace(SigningSignerType.SIGNER, SigningSignerType.BORROWER)
          .replace(SigningSignerType.NON_BORROWER, SigningSignerType.NON_BORROWER_ENTITY),
        ...rest,
      }))
    : baseOptions;

  const isSignatureOnHtml = isNotPdfFormType && hellosignTag === SigningTagType.SIGNATURE;

  const scopedOptions = isSignatureOnHtml
    ? options
    : options
        .filter(
          ({ value }) =>
            value !== SigningSignerType.ROLE_BASED &&
            value !== SigningSignerType.COMPANY_AUTHORIZED_SIGNER &&
            value !== SigningSignerType.SECURITY_GROUP,
        )
        .slice(1);

  // Appended after the signature-only narrowing so the TPO authorized signer survives it: the
  // broker signs every tag type, on PDF as well as HTML.
  return scopedOptions.concat(tpoAuthorizedSignerOptions);
};

export const getHelloSignTagOptionByParams = ({
  rule,
  hellosignTag,
  formType,
  helloSignTagsOptions,
}) =>
  helloSignTagsOptions.find(option => {
    if (option.value === hellosignTag) {
      const isSelectedOptionWithRule = option.rule?.[formType || FormType.HTML] === rule;

      switch (hellosignTag) {
        case SigningTagType.CHECKBOX:
          return rule ? isSelectedOptionWithRule : true;

        case SigningTagType.RADIO:
          return isSelectedOptionWithRule;

        default:
          return true;
      }
    }
  });

const replaceHelloSignFieldsInPdf = ({ editor, form, selectedPage }) => {
  const { parents, selectionRequirement, signerType, rootDependenceGroupId } = form;

  const updatedProps = parents.reduce(
    (acc, parent, parentIndex) => {
      acc.elements.push({
        helloSignTag: {
          selectionName: getSelectionName(parentIndex),
          rootDependenceGroupId,
          dependenceGroupId: rootDependenceGroupId,
          id: parent.id,
          isRequired: true,
          signer: signerType?.value,
          tag: parent?.hellosignTag?.value,
          value: signerType?.value,
        },
      });

      if (parent.children.length) {
        acc.dependenceGroups.push({
          rootDependenceGroupId,
          id: parent.children[0].dependenceGroupId,
          parentId: parent.id,
          requirement: { type: 'None' },
          signer: signerType?.value,
        });

        parent.children.forEach((child, childIndex) => {
          acc.elements.push({
            helloSignTag: {
              selectionName: getSelectionName(parentIndex, childIndex),
              rootDependenceGroupId,
              dependenceGroupId: child.dependenceGroupId,
              parentId: parent.id,
              isRequired: child.required,
              signer: signerType?.value,
              tag: child?.hellosignTag?.value,
              value: signerType?.value,
            },
          });
        });
      }

      acc.elements = acc.elements.map((element, index) => ({
        ...element,
        position: {
          ...getNewPdfElementPosition(index),
          page: selectedPage,
        },
      }));

      return acc;
    },
    {
      rootDependenceGroupId,
      isRemoved: parents.length === 0,
      dependenceGroups: [
        {
          id: rootDependenceGroupId,
          parentId: null,
          requirement: {
            type: selectionRequirement?.altValue,
          },
          signer: signerType?.value,
        },
      ],
      elements: [],
    },
  );

  return editor.execute(FormBuilderMethodType.INSERT_MULTIPLE_OPTIONS, {
    dataTemplateFieldType: ReactRenderingContent.SIGNATURE,
    selectedPage,
    ...updatedProps,
  });
};

const serializeSigningRules = signingRules =>
  encodeURIComponent(
    jsonStringify(
      signingRules.map((rule, idx) => ({
        id: rule.id,
        order: idx + 1,
        expression: { value: rule.expressionTree, userFriendlyValue: rule.expression },
        capacity: rule.capacity.value,
        includeInESigning: !!rule.includeInESigning,
      })),
    ),
  );

export const generateFieldHTML = (field, formData, options = {}) => {
  const { parentId, parentIndex, childIndex, isChild, signingId } = options;
  const signerType = formData.signerType?.value || formData.signerType;
  const isRoleBased = signerType === SigningSignerType.ROLE_BASED;
  const isCompanyAuthorizedSigner = signerType === SigningSignerType.COMPANY_AUTHORIZED_SIGNER;
  const isSecurityGroup = signerType === SigningSignerType.SECURITY_GROUP;
  const isSpecialSignerType = isRoleBased || isCompanyAuthorizedSigner || isSecurityGroup;

  const baseAttrs = {
    class: 'ck-widget',
    'has-custom-data-attributes': 'true',
    'data-template-field-type': ReactRenderingContent.SIGNATURE,
    'data-hellosign-tag': isSpecialSignerType
      ? SigningTagType.SIGNATURE
      : field.hellosignTag?.value || field.hellosignTag,
    'data-hellosign-signer': signerType,
    contenteditable: 'false',
  };

  baseAttrs['data-signing-use-poa-verbiage'] = String(!!formData.usePoaVerbiage);

  if (formData.displaySignatureLine) {
    baseAttrs['data-signing-display-signature-line'] = 'true';
  }

  if (isRoleBased) {
    baseAttrs['data-signing-id'] = signingId || crypto.randomUUID();
    baseAttrs['data-hellosign-required'] = 'true';
    baseAttrs['data-signing-role-engine-config'] = serializeSigningRules(formData.signingRules);
  }

  if (isCompanyAuthorizedSigner) {
    baseAttrs['data-signing-id'] = signingId || crypto.randomUUID();
    baseAttrs['data-hellosign-required'] = 'true';
    baseAttrs['data-signing-authorized-signer-mode'] =
      formData.authorizedSignerMode?.value || CompanyAuthorizedSignerMode.ANY;

    if (formData.authorizedSignerUserId) {
      baseAttrs['data-signing-authorized-signer-user-id'] = formData.authorizedSignerUserId;
    }
  }

  if (isSecurityGroup) {
    baseAttrs['data-signing-id'] = signingId || crypto.randomUUID();
    baseAttrs['data-hellosign-required'] = 'true';

    if (formData.securityGroup?.value) {
      baseAttrs['data-signing-security-group-id'] = formData.securityGroup.value;
    }
  }

  if (!isRoleBased && isChild) {
    baseAttrs['data-signing-dependencegroup-parent-id'] = parentId;
    baseAttrs['data-signing-dependencegroup-id'] = field.dependenceGroupId;
    baseAttrs['data-signing-dependencegroup-name'] = getSelectionName(parentIndex, childIndex);
    baseAttrs['data-signing-dependencegroup-requirement'] = 'none';
    baseAttrs['data-hellosign-required'] = field.required ? 'true' : 'false';
  }

  if (!isRoleBased && !isChild) {
    baseAttrs['data-signing-id'] = parentId;
    baseAttrs['data-signing-dependencegroup-id'] = field.dependenceGroupId;
    baseAttrs['data-signing-dependencegroup-name'] = getSelectionName(parentIndex);
    baseAttrs['data-signing-dependencegroup-requirement'] = formData.selectionRequirement?.value;
    baseAttrs['data-hellosign-required'] = 'true';
  }

  const attrs = Object.entries(baseAttrs)
    .map(([k, v]) => `${k}="${v}"`)
    .join(' ');

  return `<span ${attrs}><span class="content"></span></span>`;
};

const getTempDiv = editor => {
  const currentHTML = editor.getData();

  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = currentHTML;

  return tempDiv;
};

const removeHelloSignFields = (tempDiv, dependenceGroupId) => {
  const existingHelloSignFields = tempDiv.querySelectorAll(
    `[data-signing-dependencegroup-id="${dependenceGroupId}"]`,
  );

  existingHelloSignFields.forEach(field => field.remove());
};

const replaceHelloSignFieldsInHTML = ({ editor, form }) => {
  editor.commands.get(FormBuilderOperationType.UNDO).execute();
  const currentHTML = editor.getData();

  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = currentHTML;

  const helloSignFieldsHTML = [];

  form.parents.forEach(parent => {
    removeHelloSignFields(tempDiv, parent.dependenceGroupId);
    parent.children.forEach(child => removeHelloSignFields(tempDiv, child.dependenceGroupId));
  });

  form.parents.forEach((parent, parentIndex) => {
    const newFieldHTML = generateFieldHTML(parent, form, {
      parentIndex,
      parentId: parent.id,
      isRequired: true,
    });

    helloSignFieldsHTML.push(newFieldHTML);

    parent.children?.forEach((child, childIndex) => {
      const newChildHTML = generateFieldHTML(child, form, {
        isChild: true,
        parentId: parent.id,
        parentIndex,
        childIndex,
      });
      helloSignFieldsHTML.push(newChildHTML);
    });
  });

  const helloSignFieldsHTMLString = helloSignFieldsHTML.join('');

  tempDiv.insertAdjacentHTML('afterbegin', helloSignFieldsHTMLString);

  editor.setData(tempDiv.innerHTML);
};

const executeMultipleOptions = ({ isPdfFormType, isNewItem, editor, props, ...rest }) => {
  if (isNewItem && isPdfFormType) {
    editor.commands.get(FormBuilderOperationType.UNDO).execute(props);
  }

  return isPdfFormType
    ? replaceHelloSignFieldsInPdf({ editor, ...rest })
    : replaceHelloSignFieldsInHTML({ editor, ...rest });
};

const removePdfDependenceGroups = ({ editor, form, selectedPage }) => {
  editor.execute(FormBuilderMethodType.DELETE_MULTIPLE_OPTIONS, {
    dataTemplateFieldType: ReactRenderingContent.SIGNATURE,
    selectedPage,
    rootDependenceGroupId: form.rootDependenceGroupId,
  });
};

const removeHtmlDependenceGroups = ({ editor, form }) => {
  const tempDiv = getTempDiv(editor);
  const dependenceGroupsIds = form.parents.reduce((acc, parent) => {
    acc.push(parent.dependenceGroupId);
    acc.push(parent.children?.[0].dependenceGroupId);

    return acc;
  }, []);

  dependenceGroupsIds.forEach(id => removeHelloSignFields(tempDiv, id));
  editor.setData(tempDiv.innerHTML);
};

const removeDependenceGroups = ({ isPdfFormType, editor, form, selectedPage }) => {
  return isPdfFormType
    ? removePdfDependenceGroups({ editor, form, selectedPage })
    : removeHtmlDependenceGroups({ editor, form });
};

export const executeEditor = ({ editor, form, props, isEdit, isNewItem, ...editorProps }) => {
  const isRoleBased = isRoleBasedSignerType(form.signerType);
  const isCompanyAuth = isCompanyAuthorizedSignerType(form.signerType);
  const isSecGroup = isSecurityGroupSignerType(form.signerType);
  const isSpecial = isRoleBased || isCompanyAuth || isSecGroup;

  form.isMultipleOptions && !isSpecial
    ? executeMultipleOptions({ editor, form, isNewItem, props, ...editorProps })
    : editor.execute(FormBuilderMethodType.INSERT_REACT_COMPONENT, {
        ...props,
        dataTemplateFieldType: ReactRenderingContent.SIGNATURE,
        isNewItem: false,
        dataHellosignTag: isSpecial ? SigningTagType.SIGNATURE : form.hellosignTag?.value,
        dataHellosignRequired: form.required,
        dataHellosignSigner: form.signerType.value,
        dataSigningRoleEngineConfig: isRoleBased ? serializeSigningRules(form.signingRules) : '',
        dataSigningUsePoaVerbiage: form.usePoaVerbiage,
        dataSigningDisplaySignatureLine: form.displaySignatureLine,
        dataSigningAuthorizedSignerMode: isCompanyAuth
          ? form.authorizedSignerMode?.value || CompanyAuthorizedSignerMode.ANY
          : '',
        dataSigningAuthorizedSignerUserId: isCompanyAuth ? form.authorizedSignerUserId || '' : '',
        dataSigningSecurityGroupId: isSecGroup ? form.securityGroup?.value || '' : '',
        isEdit,
      });

  if (!form.isMultipleOptions && !isSpecial && form.dependenceGroupId) {
    removeDependenceGroups({
      isPdfFormType: editorProps.isPdfFormType,
      editor,
      form,
      selectedPage: editorProps.selectedPage,
    });
  }

  return;
};

export const getHelloSignField = ({ helloSignTag, isParent, rootDependenceGroupId }) => {
  const { isRequired, tag, id, ...rest } = helloSignTag;

  return {
    ...rest,
    fieldType: ReactRenderingContent.SIGNATURE,
    hellosignTag: signingTagsOptions.find(({ value }) => value === tag),
    required: isRequired,
    rootDependenceGroupId,
    ...(isParent && {
      children: [],
      id: id || crypto.randomUUID(),
    }),
  };
};

export const getSelectionName = (parentIndex, childIndex) => {
  return `Selection ${parentIndex + 1}${isNumber(childIndex) ? `.${childIndex + 1}` : ''}`;
};

export const removeTagByParentId = (html, parentId) => {
  if (!html || !parentId) {
    return html;
  }

  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  const childSelectors = [
    `span.ck-widget[data-signing-dependencegroup-parent-id="${parentId}"]`,
    `span[data-signing-dependencegroup-parent-id="${parentId}"]`,
  ];

  childSelectors.forEach(selector => {
    tempDiv.querySelectorAll(selector).forEach(element => {
      element.remove();
    });
  });

  tempDiv.querySelectorAll('p').forEach(p => {
    if (!p.innerHTML.trim() || p.innerHTML === '&nbsp;') {
      p.remove();
    }
  });

  return tempDiv.innerHTML;
};

export const deserializeSigningRules = ({
  configJson,
  isNewExpressionsParsingLogicEnabled = false,
}) => {
  const decoded = decodeURIComponent(configJson);
  const parsed = parseJSONObject(decoded);

  if (!Array.isArray(parsed)) {
    return [];
  }

  return parsed
    .sort((a, b) => a.order - b.order)
    .map(rule => {
      const expressionTreeValue = rule.expression?.value;
      const storedUserFriendlyValue = rule.expression?.userFriendlyValue;
      const userFriendlyExpression =
        storedUserFriendlyValue ||
        UserFriendlyExpressionBuilder.build(expressionTreeValue, true, {
          isNewExpressionsParsingLogicEnabled,
        });

      const capacityOption = signingCapacityOptions.find(({ value }) => value === rule.capacity);

      return {
        id: rule.id || crypto.randomUUID(),
        order: rule.order,
        expression: formatExpression(userFriendlyExpression) || userFriendlyExpression || '',
        expressionTree: expressionTreeValue,
        capacity: capacityOption || signingCapacityOptions[0],
        includeInESigning: !!rule.includeInESigning,
      };
    });
};
